import React, { useMemo } from 'react'
import { toast } from 'react-toastify'

import { Grid, TextField, Typography, Button } from '@mui/material'
import { useTheme } from '@material-ui/core'

import { useAddRemovePropertyForms } from '../../hooks/useAddRemovePropertyForms'
import { StyledFormContainer, SectionTitle } from './styles'
import { User } from '../../models'
import { Selector } from '../../components'

interface Props {
    rootUser: User
    organization: number | null
}

export const RemovePropertyFormContainer = ({
    rootUser,
    organization,
}: Props) => {
    const { createRemovePropertyForm } = useAddRemovePropertyForms(organization)
    const theme = useTheme()

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault()
        await createRemovePropertyForm({
            companies: selectedProperties,
            organization: organization ?? 0,
            reason: reason,
            termination_date: termination_date || null,
        }).then(() => {
            toast.success('Property removal request sent successfully')
            setSelectedProperties([])
            setReason('')
            setTerminationDate('')
        })
    }

    const [selectedProperties, setSelectedProperties] = React.useState<
        number[]
    >([])
    const [reason, setReason] = React.useState('')
    const [termination_date, setTerminationDate] = React.useState('')

    const companyList = useMemo(() => {
        return rootUser.workspaces
            .filter(
                (workspaceUser) =>
                    workspaceUser.active_workspace.organization ===
                    organization,
            )
            .map((workspaceUser) => workspaceUser.active_workspace)
    }, [rootUser.workspaces, organization])

    return (
        <StyledFormContainer maxWidth="lg">
            <Typography variant="h4" gutterBottom>
                Request to Remove Properties
            </Typography>
            <div className="form-content">
                <form className="form-section" onSubmit={handleSubmit}>
                    <Grid container spacing={2}>
                        <Grid item xs={12}>
                            <SectionTitle variant="h6">
                                Select Properties
                            </SectionTitle>
                        </Grid>
                        <Grid item xs={12}>
                            <Selector
                                customStyle={{
                                    formControl: { flex: 1, width: '100%' },
                                }}
                                data={companyList}
                                label="Properties"
                                onChange={(e) => {
                                    setSelectedProperties(
                                        (e.target.value as unknown) as number[],
                                    )
                                }}
                                currentValue={selectedProperties}
                                getDisplayString={(v) => {
                                    return v.name
                                }}
                                searchable
                                multiple
                                required
                            />
                        </Grid>

                        <Grid item xs={12}>
                            <SectionTitle variant="h6">
                                Reason for Removal
                            </SectionTitle>
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                multiline
                                rows={4}
                                label="Reason"
                                value={reason}
                                onChange={(e) => setReason(e.target.value)}
                                required
                            />
                        </Grid>

                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                type="date"
                                label="Termination Date"
                                value={termination_date}
                                onChange={(e) =>
                                    setTerminationDate(e.target.value)
                                }
                                InputLabelProps={{ shrink: true }}
                            />
                        </Grid>

                        {/* Submit Button */}
                        <Grid item xs={12}>
                            <Button
                                type="submit"
                                variant="contained"
                                color="primary"
                                size="large"
                                fullWidth
                                sx={{ mt: 2 }}
                                style={{
                                    textTransform: 'none',
                                    backgroundColor: theme.palette.primary.dark,
                                    color: theme.palette.primary.contrastText,
                                }}
                            >
                                Submit
                            </Button>
                        </Grid>
                    </Grid>
                </form>
            </div>
        </StyledFormContainer>
    )
}
